import type { UserRole } from '@/lib/types'

/**
 * WHO SEES WHICH SCREEN, ON THE SERVICE SIDE.
 *
 * A deliberate twin of src/lib/nav.ts rather than a shared file. The two sides
 * have different screens, different roles and different reasons, and the one
 * thing the Materials nav's own header warns about is a permission being
 * widened in one place while the menu is governed by another. Two small honest
 * lists beat one clever shared one.
 *
 * Plain module, no React, so the tests read it directly.
 */

export const SERVICE_ROUTE_ROLES: Record<string, UserRole[]> = {
  // Everybody who gets in here gets the dashboard. What it SHOWS differs by
  // role - see ServiceDashboard - but the door is the same door.
  '/service': ['site_engineer', 'site_construction_manager', 'project_manager',
               'procurement_manager', 'procurement_head', 'project_head',
               'cmd', 'cxo', 'accounts_spoc', 'admin'],

  // The contract itself. Everyone who can reach this side may READ a work
  // order for a site they can reach - the database decides which sites. Only
  // Procurement may write one, and that is enforced by RLS and by the buttons
  // on the screen, not by this list.
  '/service/work-orders': ['site_engineer', 'site_construction_manager', 'project_manager',
                           'procurement_manager', 'procurement_head', 'project_head',
                           'cmd', 'cxo', 'accounts_spoc', 'admin'],

  // The Measurement Book. Accounts and the board have no business in the
  // book itself - they read the bill that comes out of it.
  '/service/measurements': ['site_engineer', 'site_construction_manager',
                            'project_manager', 'procurement_manager',
                            'procurement_head', 'project_head', 'admin'],

  // The hire log sheet - 0214. The same people as the Measurement Book: it is
  // the same job for machines instead of work, signed by the same two roles.
  '/service/hire-logs': ['site_engineer', 'site_construction_manager',
                         'project_manager', 'procurement_manager',
                         'procurement_head', 'project_head', 'admin'],

  // Variations change a contract's value, so the people who approve them and
  // the people who pay for them both need to see them.
  '/service/variations': ['site_construction_manager', 'project_manager',
                          'procurement_manager', 'procurement_head', 'project_head',
                          'cmd', 'cxo', 'accounts_spoc', 'admin'],

  // The schedule of rates. Read by anyone writing or checking an order;
  // written by Procurement and the Head of Stores (who owns the material
  // master and keeps the two consistent).
  '/service/work-items': ['site_engineer', 'site_construction_manager', 'project_manager',
                          'procurement_manager', 'procurement_head', 'project_head',
                          'cmd', 'cxo', 'admin'],

  // Money. Phase 4 and 6 fill these in; the doors are defined now so the
  // dashboard can link to them and the roles are decided once.
  '/service/ra-bills': ['site_construction_manager', 'project_manager',
                        'procurement_manager', 'procurement_head', 'project_head',
                        'cmd', 'cxo', 'accounts_spoc', 'admin'],
  '/service/contractor-payables': ['procurement_manager', 'procurement_head',
                                   'project_head', 'cmd', 'cxo', 'accounts_spoc', 'admin'],
  // Payments made to contractors — 0141. The same list as the payables they
  // settle, and deliberately so: a person who may see what is owed may see what
  // has been paid against it, or the two screens tell him different halves of
  // one story. Recording a payment is narrower still and is enforced by
  // rpc_ra_record_payment, not by this list — this is the door, not the till.
  '/service/payments': ['procurement_manager', 'procurement_head',
                        'project_head', 'cmd', 'cxo', 'accounts_spoc', 'admin'],
  // The statutory pack. Reverse-charge GST, TDS under s.393(1), retention
  // held, advances outstanding, input credit, and work certified but not yet
  // invoiced. Accounts owns it; the board reads it.
  '/service/statutory': ['accounts_spoc', 'procurement_head', 'project_head',
                         'cmd', 'cxo', 'admin'],

  // Reverse charge — 0146 and 0147. The same people as the statutory pack, and
  // deliberately so: it is the same tax seen at the moment somebody has to act
  // on it rather than at the moment it is reported. Procurement is on the list
  // because it is Procurement that knows whether a security agency is a firm
  // or an LLP, and that one fact decides the treatment. Only an admin can move
  // the switch, and only Accounts or Procurement can raise a document —
  // enforced by rpc_rcm_raise_self_invoice and by the row level security, not
  // by this list, which is the door.
  '/service/reverse-charge': ['accounts_spoc', 'procurement_manager', 'procurement_head',
                              'project_head', 'cmd', 'cxo', 'admin'],

  // Retention releases — 0154. Wider than the raise list on purpose: the
  // Project Manager and the Site Construction Manager cannot raise a release
  // and cannot approve one, but they are the people who know whether the snag
  // list was cleared, and they are asked about it. A door you cannot open is
  // a question somebody has to ask by telephone.
  //
  // Raising is rpc_retention_raise (Accounts, Projects Head, Procurement Head,
  // admin), approving is the chain (Project Head then CMD) and paying is
  // rpc_retention_record_payment (Accounts, admin). None of that is enforced
  // by this list, which is the door.
  // Mobilisation advances — 0163. The same list as retention releases, and
  // for the same reason: the Project Manager cannot raise or approve one on
  // his own, but he is the man who knows whether the contractor is actually
  // on site, and he is asked. Raising is rpc_adv_raise, approving is the
  // chain, paying is rpc_adv_record_payment — none of it enforced here.
  // Credit notes — 0165. Wider than the raise list: a Store Man does not
  // raise one, but "material supplied by OVAL, recoverable" is HIS issue note
  // that started it, and he is entitled to see where it went.
  '/service/credit-notes': ['accounts_spoc', 'project_manager', 'project_head',
                            'site_construction_manager', 'site_engineer',
                            'procurement_manager', 'procurement_head',
                            'stores_head', 'cmd', 'cxo', 'admin'],

  '/service/advances': ['accounts_spoc', 'project_manager', 'project_head',
                        'site_construction_manager', 'procurement_manager',
                        'procurement_head', 'cmd', 'cxo', 'admin'],

  '/service/retention-releases': ['accounts_spoc', 'project_manager', 'project_head',
                                  'site_construction_manager', 'procurement_head',
                                  'cmd', 'cxo', 'admin'],

  // THE MASTERS, ON THIS SIDE OF THE HOUSE — added 14-09-2026.
  //
  // Contractors, sites and units are read constantly while writing or checking
  // a work order: is this contractor GST registered, what is his PAN, who is
  // the Project Manager of that site, is SFT or SQM the code. All three were
  // only on the Materials side, so answering any of them meant leaving the
  // module and coming back.
  //
  // READ ONLY, deliberately. Maintaining a vendor stays where it has always
  // been - the vendor master on the Materials side, owned by Procurement - and
  // duplicating an editor here would mean two screens that must agree forever.
  // Everyone who can reach the Service side can open this; the tables
  // themselves are readable by every signed-in user already, so this widens
  // nothing.
  '/service/masters': ['site_engineer', 'site_construction_manager', 'project_manager',
                       'procurement_manager', 'procurement_head', 'project_head',
                       'cmd', 'cxo', 'accounts_spoc', 'admin'],

  // Service settings, 29-09-2026 (0184). The System Administrator alone; the
  // database refuses anybody else in rpc_set_service_setting as well.
  '/service/settings': ['admin'],
}

export function canSeeServiceRoute(role: UserRole | null | undefined, to: string): boolean {
  if (!role) return false
  const allowed = SERVICE_ROUTE_ROLES[to]
  // A route absent from the map is open to anyone who reached this side at
  // all. Same rule as the Materials nav, and the same reason: a new screen
  // should not be invisible because somebody forgot a list.
  return allowed ? allowed.includes(role) : true
}

/**
 * What a person may DO, as opposed to what they may open. Mirrors the row
 * level security in migrations 0118 and 0119 exactly - the screen hides what
 * the database would refuse anyway, so nobody meets a dead end.
 */
export const SERVICE_CAN: Record<string, UserRole[]> = {
  // project_head and project_manager added 14-09-2026.
  //
  // Both may raise a work order, and in both cases the stage that is their own
  // is CLEARED ON RAISING - stage 3 for the Project Head, stage 2 for the
  // Project Manager - recorded as a stand-in with a comment saying so. Stage 1,
  // the four-eyes stage, never clears itself for anybody.
  //
  // A Project Manager may only raise one for a site he is named on; that is
  // can_access_site, not this list. All of it is enforced in rpc_works_action
  // and in the row level security, not here - this decides which buttons to
  // draw.
  // 0175, board 22-09-2026: PROJECTS ONLY. procurement_manager and
  // procurement_head were here and are not any more.
  //
  // The reasoning is the note four lines further down, applied one document
  // earlier: billing is the project's chain, and so is the contract the
  // billing hangs off. Procurement keeps material purchase orders, which are
  // untouched.
  //
  // This list and the wo_insert / wo_update policies in 0175 must say the same
  // thing. If they ever drift, the database wins and the user gets a button
  // that draws and then fails — which is how you turn a permission decision
  // into a support call.
  //
  // 0180, board 25-09-2026: REVERSED. Procurement Managers and the Head of
  // Procurement may raise a work order again, for any site, beside Projects.
  // The chain is untouched: stage 1 is still the OTHER Procurement Manager,
  // and stage 1 never clears itself for anybody. The policies in 0180 carry
  // the same five roles as this list.
  createWorkOrder:   ['procurement_manager', 'procurement_head',
                      'project_head', 'project_manager', 'admin'],
  recordMeasurement: ['site_engineer', 'site_construction_manager', 'admin'],
  certifyMeasurement:['site_construction_manager', 'admin'],
  raiseVariation:    ['procurement_manager', 'procurement_head', 'admin'],
  // The Schedule of Rates. Board 14-09-2026: Procurement and the Project Head
  // maintain it; the CMD, the CXO and Project Managers read it. stores_head has
  // the right in the database because he owns the material master and the two
  // are kept consistent - he is not listed here because he cannot reach this
  // side of the application at all (see SERVICE_ROLES), and a permission he can
  // never exercise on a screen he can never open is noise in a list whose whole
  // job is to be read.
  manageWorkItems:   ['procurement_manager', 'procurement_head', 'project_head', 'admin'],

  // Board 16-09-2026: billing is the project's chain, not Procurement's.
  // Raised by the Project Manager of the site or by the Project Head; approved
  // by PM -> Project Head -> CMD. Accounts is deliberately off the path - the
  // function is short-staffed and putting it on every monthly bill would stop
  // bills moving rather than improve a deduction.
  prepareRaBill:     ['project_manager', 'project_head', 'admin'],

  // Recording that money left, and attaching the bank advice. Wider than
  // raising a bill, because the person who pays is not the person who bills.
  recordPayment:     ['accounts_spoc', 'procurement_manager', 'procurement_head',
                      'project_head', 'admin'],

  // The standard clauses, per pricing basis. "Procurement managers / PM /
  // Project Head", as asked for on 16-09-2026.
  manageTerms:       ['procurement_manager', 'procurement_head', 'project_manager',
                      'project_head', 'admin'],
}

export const serviceCan = (role: UserRole | null | undefined, what: string): boolean =>
  !!role && (SERVICE_CAN[what] ?? []).includes(role)
